<template>
  <div
    ref="stepRef"
    class="pet-modal-wrapper"
    :class="mainClasses"
  >
    <div
      role="document"
      class="pet-modal appearance-surface"
      :class="classes"
    >
      <div class="pet-modal__top">
        <basic-close-button
          v-if="$props.closeable"
          aria-label="Close Modal"
          :size="closeButtonSize"
          @click="instance.exposed.close()"
        />
      </div>

      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeMount, onMounted, ref } from 'vue'

import events$ from '@/services/Events'

import BasicCloseButton from '@/components/Basic/CloseButton'
import useBrowser from '@/hooks/useBrowser'

import { EVENT_MODAL } from '@/config/events'

// HOOKS
const { isSmallOnlyViewport } = useBrowser()

// INIT
const animation = ref({
  timeout: 300,
  css: '300ms',
})
const props = defineProps({
  closeable: {
    type: Boolean,
    default: true,
  },

  hasGradient: {
    type: Boolean,
    default: true,
  },

  id: {
    type: String,
    required: true,
  },

  modalActions: {
    type: Boolean,
    default: true,
  },

  primaryLabel: {
    type: String,
    default: null,
  },

  primaryTrackAs: {
    type: String,
    default: null,
  },

  primaryVisible: {
    type: Boolean,
    default: true,
  },

  secondaryLabel: {
    type: String,
    default: null,
  },

  secondaryTrackAs: {
    type: String,
    default: null,
  },

  secondaryVisible: {
    type: Boolean,
    default: true,
  },

  tertiaryLabel: {
    type: String,
    default: null,
  },

  size: {
    validator(value) {
      return ['mini', 'midi', 'maxi', 'full'].includes(value)
    },
    default: 'midi',
  },

  stepTitle: {
    type: String,
    default: null,
  },

  type: {
    /*
     *  passive:            no CTA
     *  transactional:      validate decisions, requires action to be completed and closed
     *  acknowledgement:    system requires the user to acknowledge the presented information
     *  progress:           navigate via next and back
     *  raw:
     */
    validator(value) {
      return ['passive', 'transactional', 'acknowledgement', 'progress', 'raw'].includes(value)
    },
    default: 'passive',
  },
})

// DATA
const instance = ref()
const stepRef = ref()
const fromHeight = ref()

// COMPUTED
const effectiveSize = computed(() => {
  if (isSmallOnlyViewport.value) {
    return 'maxi'
  }
  return props.size
})

const classes = computed(() => {
  return {
    [`pet-modal--${effectiveSize.value}`]: true,
    [`pet-modal--${props.type}`]: true,
  }
})

const closeButtonSize = computed(() => {
  return ['maxi', 'full'].includes(effectiveSize.value) ? 'lg' : 'sm'
})

const mainClasses = computed(() => {
  return {
    'animation-initial': instance.value.exposed.isInitialOpen.value === true,
    'animation-step': instance.value.exposed.isInitialOpen.value === false,
    'the-grid': !['maxi', 'full'].includes(effectiveSize.value),
    [`pet-modal-wrapper--${effectiveSize.value}`]: true,
  }
})

// METHODS

// LIFECYCLE HOOKS
onBeforeMount(async () => {
  instance.value = getCurrentInstance().parent.parent.parent

  // @note: set the initialFlag to flag, used for animations
  if (instance.value.exposed.isInitialOpen.value) {
    window.setTimeout(() => {
      instance.value.exposed.isInitialOpen.value = false
    }, animation.value.timeout)
  }

  events$.emit(EVENT_MODAL.UPDATE, {
    modalName: instance.value.exposed.id + '-' + props.id,
  })

  await new Promise(r => setTimeout(r, 100))

  fromHeight.value = instance.value.exposed.doIt(true)
})

onMounted(() => {
  fromHeight.value = instance.value.exposed.doIt()
})

// EPILOGUE
defineExpose({
  ...props,
})
</script>

<style name="animations" scoped>
.step-enter-active {
  transition-duration: 1s;
}

.step-leave-active {
  transition-duration: 1s;
}

.step-leave-active :deep(.pet-modal__footer),
.step-leave-active :deep(.pet-modal__top) {
  transition: all 0.3s ease;
}

.step-leave-active :deep(.scrollable) {
  transition: all 0.3s ease;
  transition-delay: 0.3s;
}

.step-enter-active :deep(.scrollable) {
  transition: all 0.3s ease;
}

.step-enter-active :deep(.pet-modal__footer),
.step-enter-active :deep(.pet-modal__top) {
  transition: all 0.3s ease;
  transition-delay: 0.3s;
}

.step-enter-from :deep(.scrollable) {
  transform: translateX(100%);
}

.step-leave-to :deep(.scrollable) {
  transform: translateX(-100%);
}

.step-enter-from :deep(.pet-modal__footer) {
  transform: translateY(100%);
}

.step-leave-to :deep(.pet-modal__footer) {
  transform: translateY(100%);
}

.step-enter-from :deep(.pet-modal__top) {
  transform: translateY(-100%);
}

.step-leave-to :deep(.pet-modal__top) {
  transform: translateY(-100%);
}
</style>

<style scoped>
.pet-modal-wrapper {
  position: relative;
  height: 100%;
  align-items: center;

  &:not(.the-grid) {
    display: flex;
    align-items: flex-end;
    width: 100%;
  }
}

.pet-modal {
  display: flex;
  flex-direction: column;
  z-index: var(--pet-stack-level-popup-menu);
  background-color: var(--surface);
  box-shadow: var(--elevation-level-3);
  border-radius: var(--modal-border-radius);
  overflow: hidden;

  &--mini {
    --modal-border-radius: var(--fixed-border-radius-fix-03);

    grid-column: 1 / -1;
    max-height: 48dvh;
  }

  &--midi {
    --modal-border-radius: var(--fixed-border-radius-fix-03);

    grid-column: 1 / -1;
    max-height: 78dvh;
  }

  &--maxi {
    --modal-border-radius: var(--border-radius-res-l) var(--border-radius-res-l) var(--fixed-border-radius-fix-00)
      var(--fixed-border-radius-fix-00);

    width: 100%;
    max-height: 96dvh;
  }

  &--full {
    display: inline-block;
    width: 100%;
    height: 100dvh;
  }

  &__top {
    display: flex;
    justify-content: flex-end;
    padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive)
      var(--dotcom-responsive-spacing-res-xs);
  }

  &__actions {
    padding: var(--fixed-spacing-fix-02) var(--dotcom-responsive-gutter-responsive)
      var(--dotcom-responsive-gutter-responsive);

    .transactional,
    .passive {
      display: flex;
      flex-direction: column;
      gap: var(--fixed-spacing-fix-02);
    }

    .acknowledgement {
      display: flex;
      justify-content: center;
    }

    .progress {
      display: flex;
      flex-direction: column;
      gap: var(--fixed-spacing-fix-02);

      &__navigation {
        display: flex;
        flex-direction: column;
        gap: var(--fixed-spacing-fix-02);
      }
    }

    &.raw {
      display: flex;
      flex-direction: column;
      gap: var(--fixed-spacing-fix-02);
      justify-content: space-between;
    }
  }
}

.pet-modal__title {
  margin: 0 0 var(--dotcom-responsive-spacing-res-xxs) 0;
}

.pet-modal--mini .pet-modal__title {
  margin: 0 0 var(--fixed-spacing-fix-02) 0;
}

.pet-modal--maxi .pet-modal__title,
.pet-modal--maxi .pet-modal__body,
.pet-modal--full .pet-modal__title,
.pet-modal--full .pet-modal__body {
  grid-column: 1 / -1;
}

.pet-modal__top {
  display: flex;
  justify-content: flex-end;
  padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive)
    var(--dotcom-responsive-spacing-res-xs);
}

.pet-modal__footer.the-grid > * {
  grid-column: 1 / -1;
}

.pet-modal__footer.the-grid .pet-modal__actions {
  padding: var(--fixed-spacing-fix-02) 0 var(--dotcom-responsive-gutter-responsive);
}

@media (--v-medium) {
  .pet-modal {
    &--mini {
      width: 320px;
      margin: 0 auto;
    }

    &__actions {
      .passive {
        flex-direction: row;
        justify-content: flex-end;
      }

      .transactional,
      &.raw {
        flex-direction: row-reverse;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: var(--fixed-spacing-fix-06);
      }
    }
  }
}

@media (--v-large) {
  .pet-modal {
    &--midi {
      grid-column: 3 / 11;
    }

    &__actions {
      .acknowledgement {
        justify-content: flex-end;
      }

      .progress {
        flex-direction: row-reverse;
        justify-content: space-between;
        gap: var(--fixed-spacing-fix-06);

        &__navigation {
          flex-direction: row-reverse;
          gap: var(--fixed-spacing-fix-06);
        }
      }
    }
  }
}

@media (--v-wide) {
  .pet-modal {
    &--mini {
      width: auto;
      margin: 0;
      grid-column: 5 / 9;
    }
  }
}
</style>
